/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Transcript, TranscriptItem } from '@/types/transcript';
import { arrayToVTT, buildParagraphsFromDeepgram, toJson } from '@/utils/transcript';
import { downloadFile } from '@/utils';
import { useGetProjectVideoTranscript, useUpdateProjectVideoTranscript } from '@/adapters/projects';
import Project from '@/types/project';
import { usePostTranscript } from '@/adapters/users';
import { DeepgramResponse, Words } from '@/entities/transcript';
export interface TranscriptContextValue {
  transcript?: Transcript | null;
  currentTime?: number | null;
  editable: boolean;
  onSelectItem(item: TranscriptItem): void;
  onUpdateItem(item: TranscriptItem, text: string): void;
  edit(): void;
  save(): void;
  cancel(): void;
  undo(): void;
  redo(): void;
  exportToTXT(): void;
  exportToJSON(): void;
  exportToVTT(): void;
  isLoadingTranscript: boolean;
  isUpdatingProjectVideoTranscript: boolean;
  transcriptContainerRef: any;
  timeCodes: {
    start: number;
    end: number;
  } | null;
}
export const TranscriptContext = React.createContext<TranscriptContextValue>({
  transcript: null,
  editable: false,
  onSelectItem: () => undefined,
  onUpdateItem: () => undefined,
  edit: () => undefined,
  save: () => undefined,
  cancel: () => undefined,
  undo: () => undefined,
  redo: () => undefined,
  exportToTXT: () => undefined,
  exportToJSON: () => undefined,
  exportToVTT: () => undefined,
  isLoadingTranscript: false,
  isUpdatingProjectVideoTranscript: false,
  transcriptContainerRef: null,
  timeCodes: null,
});
export interface TranscriptProviderProps {
  children: React.ReactNode;
  project: Project;
  videoId: string;
  segmentId?: string;
  videoRef?: React.RefObject<HTMLVideoElement>;
  currentTime?: number | null;
  setTimeStamp?(timeStamp: number): void;
  containerRef: React.RefObject<HTMLDivElement>;
  seg?: boolean;
  timeCodes?: {
    start: number;
    end: number;
  };
}

export const parseS3Path = (s3Path: string): { bucketName: string; videoKey: string } => {
  if (!s3Path) {
    return { bucketName: '', videoKey: '' };
  }
  if (!s3Path.startsWith('s3://')) {
    throw new Error("Invalid S3 path. It must start with 's3://'");
  }

  // Remove "s3://" and split the string at the first slash
  const pathWithoutProtocol = s3Path.slice(5);
  const firstSlashIndex = pathWithoutProtocol.indexOf('/');

  if (firstSlashIndex === -1) {
    throw new Error('Invalid S3 path. Missing key after bucket name.');
  }

  const bucketName = pathWithoutProtocol.substring(0, firstSlashIndex);
  const videoKey = pathWithoutProtocol.substring(firstSlashIndex + 1);

  return { bucketName, videoKey };
};
export const TranscriptProvider: React.FC<TranscriptProviderProps> = ({
  children,
  project,
  videoId,
  videoRef,
  currentTime,
  setTimeStamp,
  containerRef,
  seg,
  timeCodes,
  segmentId,
}: TranscriptProviderProps) => {
  const segmentName = localStorage.getItem('selectedSegmentName') || '';
  const { data: completeTranscript, isLoading: isLoadingTranscript } = useGetProjectVideoTranscript(
    project.id,
    videoId,
  );

  const currentProject = useMemo(
    () => project.edits.find((edit) => edit.id === segmentId && edit.status === 'Processed'),
    [project, segmentId],
  );
  const { data: transcriptData } = usePostTranscript(parseS3Path(currentProject?.pathKey || ''));
  const { mutateAsync: updateProjectVideoTranscript, isLoading: isUpdatingProjectVideoTranscript } =
    useUpdateProjectVideoTranscript(project.id, videoId);
  const [editableTranscript, setEditableTranscript] = useState<Transcript | undefined>();
  const transcriptItemMapRef = useRef<Record<number, TranscriptItem>>({});
  const [editable, setEditable] = useState<boolean>(false);

  const transcript = useMemo(() => {
    if (completeTranscript) {
      return completeTranscript;
    } else if (transcriptData) {
      const transcriptResponse = transcriptData.results.channels[0].alternatives[0];
      return {
        transcript: transcriptResponse?.transcript || '',
        items:
          transcriptResponse?.words?.map((word: any) => ({
            text: word.punctuated_word || word.word,
            ...word,
          })) || [],
      } as Transcript;
    }
  }, [completeTranscript, transcriptData]);

  useEffect(() => {
    if (transcript) {
      transcriptItemMapRef.current = transcript.items.reduce(
        (result: Record<number, TranscriptItem>, item) => {
          if (item.start) {
            result[item.start] = { ...item };
          }
          return result;
        },
        {},
      );
      setEditableTranscript({
        transcript: transcript.transcript,
        items: transcript.items.map((item) => ({ ...item })),
      });
    }
  }, [transcript, transcriptItemMapRef]);
  const edit = useCallback(() => {
    setEditable(true);
  }, []);
  const save = useCallback(async () => {
    const items = Object.values(transcriptItemMapRef.current).filter((item) => !!item.text.trim());
    items.sort(function (a, b) {
      return a.start - b.start;
    });
    await updateProjectVideoTranscript({
      transcript: items.map((item) => item.text.trim()).join(' '),
      items: items.map((item) => ({
        ...item,
        punctuated_word: item.text.trim(),
        word: item.text.trim(),
      })),
      fileName:
        currentProject?.pathKey?.split('/').pop()?.replace('.mp4', '.transcript.json') || '',
    });
    setEditable(false);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, [updateProjectVideoTranscript, transcriptItemMapRef]);
  const cancel = useCallback(() => {
    if (transcript) {
      setEditableTranscript({
        transcript: transcript.transcript,
        items: transcript.items.map((item) => ({ ...item })),
      });
    }
    setEditable(false);
  }, [transcript, setEditable]);
  const onSelectItem = useCallback(
    (item: TranscriptItem) => {
      if (videoRef && videoRef.current) {
        videoRef.current.currentTime = item.start;
        setTimeStamp && setTimeStamp(item.start);
      }
    },
    [videoRef],
  );
  const onUpdateItem = useCallback(
    (item: TranscriptItem, text: string) => {
      transcriptItemMapRef.current[item.start].text = text.trim();
    },
    [transcriptItemMapRef],
  );
  const undo = useCallback(() => {
    //
  }, []);
  const redo = useCallback(() => {
    //
  }, []);
  const getExportFilename = (ext: string): string => {
    const cleanedSegment = segmentName || 'original';
    return `${cleanedSegment}.${ext}`;
  };
  const exportToTXT = (): void => {
    if (!project || !transcript) {
      return;
    }
    const txt = buildParagraphsFromDeepgram(transcriptData as unknown as DeepgramResponse);
    downloadFile(new Blob([txt], { type: 'text/plain' }), getExportFilename('txt'));
  };
  const exportToJSON = (): void => {
    if (!project || !transcript) {
      return;
    }
    const txt = toJson(transcript);
    downloadFile(txt, getExportFilename('json'));
  };
  const exportToVTT = (): void => {
    if (!project || !transcript) {
      return;
    }
    const vtt = arrayToVTT(transcript.items as unknown as Words[]);
    downloadFile(vtt, getExportFilename('vtt'));
  };
  return (
    <TranscriptContext.Provider
      value={{
        transcript: transcriptData ? transcript : editableTranscript,
        currentTime,
        editable: editable,
        onSelectItem,
        onUpdateItem,
        edit,
        save,
        cancel,
        undo,
        redo,
        exportToTXT,
        exportToJSON,
        exportToVTT,
        isLoadingTranscript,
        isUpdatingProjectVideoTranscript,
        transcriptContainerRef: containerRef,
        timeCodes: null,
      }}
    >
      {children}
    </TranscriptContext.Provider>
  );
};
